import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { Box, Divider, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { useContext, useState } from 'react';

import { UserContext } from '~/auth/userContext';
import { HeaderField } from '~/components/common/HeaderField';
import type { Invoice, LineItem } from '~/data/schemas/invoice';
import { pageBandDividerColor } from '~/theme';
import { formatCurrency } from '~/utils/format';

/** A long bill shouldn't push the tab strip off the screen: roughly five rows, then the list scrolls in place. */
const LINE_ITEMS_MAX_HEIGHT = 132;

/** `null` is the order the invoice itself lists them in. */
type AmountSort = 'asc' | 'desc' | null;

const SORT_ICON = {
  asc: <ArrowUpwardIcon fontSize="inherit" />,
  desc: <ArrowDownwardIcon fontSize="inherit" />,
} as const;

/** The icon alone carries the state visually; this is the same thing in words. */
const SORT_LABEL = {
  none: 'Sort by amount',
  asc: 'Sorted by amount, lowest first',
  desc: 'Sorted by amount, highest first',
} as const;

/**
 * The lines, largest-first on the first click and then flipping. Unsorted until asked:
 * the document is usually open beside this, and reordering by default would break the
 * correspondence between the two.
 */
const sortLineItems = (lineItems: LineItem[], sort: AmountSort): LineItem[] => {
  if (sort === null) return lineItems;
  return [...lineItems].sort((a, b) =>
    sort === 'asc' ? a.amount - b.amount : b.amount - a.amount,
  );
};

/**
 * What the invoice says, as two header fields: its description beside its line items —
 * what the property, provider and AI verdict above them are read against.
 */
export function InvoiceSummaryFields({
  invoice,
  stacked = false,
}: {
  invoice: Invoice | undefined;
  stacked?: boolean;
}) {
  const { currencyCode } = useContext(UserContext);
  const [amountSort, setAmountSort] = useState<AmountSort>(null);
  const lineItems = invoice?.lineItems ?? [];

  // The invoice's own figure when it has one; otherwise what its lines add up
  // to, so the column always foots.
  const total = invoice?.proposedAmount ?? lineItems.reduce((sum, item) => sum + item.amount, 0);

  const sortedLineItems = sortLineItems(lineItems, amountSort);

  return (
    <Box
      display="grid"
      gridTemplateColumns={stacked ? '1fr' : { xs: '1fr', md: '1fr auto 1fr' }}
      columnGap={4}
      rowGap={2}
      alignItems="start"
    >
      <HeaderField label="Description">
        {invoice ? (
          <Typography variant="body2">{invoice.description || 'No description.'}</Typography>
        ) : (
          <Skeleton width="80%" />
        )}
      </HeaderField>

      <Divider
        orientation="vertical"
        flexItem
        sx={{
          display: stacked ? 'none' : { xs: 'none', md: 'block' },
          borderColor: pageBandDividerColor,
        }}
      />

      <HeaderField
        label="Line Items"
        action={
          invoice && lineItems.length > 0 ? (
            <Tooltip title={SORT_LABEL[amountSort ?? 'none']}>
              <IconButton
                aria-label={SORT_LABEL[amountSort ?? 'none']}
                onClick={() => setAmountSort((sort) => (sort === 'desc' ? 'asc' : 'desc'))}
                sx={{
                  // Padded for a usable target, then pulled back out of the layout by the same amount,
                  // so the label row keeps the height of a bare caption.
                  p: 0.5,
                  my: -0.5,
                  mr: 0.5,
                  fontSize: 16,
                  color: 'text.secondary',
                }}
              >
                {amountSort ? SORT_ICON[amountSort] : <SwapVertIcon fontSize="inherit" />}
              </IconButton>
            </Tooltip>
          ) : null
        }
      >
        {invoice ? (
          lineItems.length > 0 ? (
            <>
              <Stack
                divider={<Divider />}
                sx={{ maxHeight: LINE_ITEMS_MAX_HEIGHT, overflowY: 'auto', pr: 1 }}
              >
                {sortedLineItems.map((item, index) => (
                  <Box
                    key={item.id ?? index}
                    display="flex"
                    gap={2}
                    justifyContent="space-between"
                    py={0.5}
                  >
                    <Typography variant="body2" minWidth={0}>
                      {item.description}
                    </Typography>
                    <Typography variant="body2" flexShrink={0}>
                      {formatCurrency({ value: item.amount, currencyCode })}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              <Box
                display="flex"
                gap={2}
                justifyContent="space-between"
                alignItems="baseline"
                pt={0.75}
                mr={1}
                borderTop="2px solid"
                borderColor="divider"
              >
                <Typography variant="subtitle1" fontWeight={700}>
                  Total
                </Typography>
                <Typography variant="subtitle1" fontWeight={700}>
                  {formatCurrency({ value: total, currencyCode })}
                </Typography>
              </Box>
            </>
          ) : (
            <Typography variant="body2">No line items.</Typography>
          )
        ) : (
          <Stack gap={0.25}>
            <Skeleton width="90%" />
            <Skeleton width="90%" />
            <Skeleton width="60%" />
          </Stack>
        )}
      </HeaderField>
    </Box>
  );
}
